import React, { useState, useEffect } from 'react';
import { Mail, TrendingUp, MousePointer, UserMinus, BarChart2, RefreshCw, Send } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import analyticsAPI from '../api/analytics';
import campaignsAPI from '../api/campaigns.js';

/* ── Design tokens ──────────────────────────────────────────────── */
const GLASS = {
  background: 'rgba(255,255,255,0.03)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 16,
};

const CAT_COLORS = {
  promo: '#EC4899', newsletter: '#A855F7', announcement: '#FF8C42',
  'thank-you': '#00D9FF', event: '#39FF14', general: '#0080FF',
  welcome: '#00FFC8', followup: '#FFD700', custom: '#A855F7',
};

const STAT_META = [
  { key: 'totalSent',         label: 'Total Sent',   color: '#00D9FF', icon: Send,         suffix: '',  subKey: null },
  { key: 'openRate',          label: 'Open Rate',    color: '#39FF14', icon: TrendingUp,   suffix: '%', subKey: 'totalOpened',    subLabel: 'unique opens' },
  { key: 'clickRate',         label: 'Click Rate',   color: '#FF8C42', icon: MousePointer, suffix: '%', subKey: 'totalClicked',   subLabel: 'unique clicks' },
  { key: 'unsubscribeRate',   label: 'Unsubs',       color: '#EC4899', icon: UserMinus,    suffix: '%', subKey: 'totalUnsubscribed', subLabel: 'total' },
];

const DAYS_OPTIONS = [7, 14, 30, 60, 90];

const fmt = (n, suffix = '') => (n === null || n === undefined) ? '—' : `${n}${suffix}`;

const STATUS_STYLE = {
  draft:     { bg: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.35)', label: 'Draft' },
  sending:   { bg: 'rgba(255,200,0,0.12)',   color: '#ffc800',               label: 'Sending' },
  sent:      { bg: 'rgba(57,255,20,0.1)',    color: '#4ade80',               label: 'Sent' },
  failed:    { bg: 'rgba(255,50,50,0.1)',    color: '#f87171',               label: 'Failed' },
  scheduled: { bg: 'rgba(100,150,255,0.1)', color: '#93c5fd',               label: 'Scheduled' },
};

/* ── Sub-components ─────────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ ...GLASS, padding: '12px 16px', fontSize: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
      <p style={{ margin: '0 0 8px', color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ margin: '3px 0', color: p.color, fontWeight: 700, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block', flexShrink: 0 }} />
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

const Skeleton = ({ h = 28, w = '100%', r = 8 }) => (
  <div style={{ height: h, width: w, background: 'rgba(255,255,255,0.05)', borderRadius: r, animation: 'pulse 1.4s ease-in-out infinite' }} />
);

/* ── Page ───────────────────────────────────────────────────────── */
const AnalyticsPage = () => {
  const [overview, setOverview]               = useState(null);
  const [timeSeries, setTimeSeries]           = useState([]);
  const [templates, setTemplates]             = useState([]);
  const [days, setDays]                       = useState(30);
  const [loading, setLoading]                 = useState(true);
  const [tsLoading, setTsLoading]             = useState(false);
  const [error, setError]                     = useState(null);
  const [campaigns, setCampaigns]             = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      analyticsAPI.getOverview().catch(() => null),
      analyticsAPI.getTimeSeries(days).catch(() => []),
      analyticsAPI.getTemplateStats().catch(() => []),
    ]).then(([ov, ts, tmpl]) => {
      setOverview(ov?.data || null);
      setTimeSeries(ts?.data || []);
      setTemplates(tmpl?.data || []);
    }).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    campaignsAPI.list({ limit: 50 })
      .then(r => setCampaigns(r.data?.campaigns || []))
      .catch(() => {})
      .finally(() => setLoadingCampaigns(false));
  }, []);

  const loadTimeSeries = async (d) => {
    setDays(d);
    setTsLoading(true);
    try {
      const res = await analyticsAPI.getTimeSeries(d);
      setTimeSeries(res?.data || []);
    } catch (e) { console.error(e); }
    finally { setTsLoading(false); }
  };

  const hasActivity = timeSeries.some(d => d.sent > 0 || d.opened > 0);

  return (
    <div style={{ minHeight: '100vh', background: '#080808', color: '#fff', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* ── Page header ────────────────────────────────────────── */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '28px 32px 20px' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BarChart2 size={18} color="#A855F7" />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>Email Analytics</h1>
              <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>Opens, clicks and campaign performance</p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '28px 32px' }}>

        {error && (
          <div style={{ ...GLASS, padding: '14px 18px', marginBottom: 24, borderColor: 'rgba(255,68,68,0.25)', color: '#f87171', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <RefreshCw size={14} /> {error}
          </div>
        )}

        {/* ── Stat cards ──────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
          {STAT_META.map(({ key, label, color, icon: Icon, suffix, subKey, subLabel }) => (
            <div key={key} style={{ ...GLASS, padding: '20px', position: 'relative', overflow: 'hidden' }}>
              {/* Top accent line */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${color}99, transparent)` }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: `${color}14`, border: `1px solid ${color}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
                  <Icon size={14} />
                </div>
              </div>
              {loading ? (
                <Skeleton h={32} r={6} />
              ) : (
                <>
                  <div style={{ fontSize: 32, fontWeight: 800, color, lineHeight: 1, letterSpacing: '-0.03em', textShadow: `0 0 24px ${color}55` }}>
                    {fmt(overview?.[key], suffix)}
                  </div>
                  {subKey && overview?.[subKey] > 0 && (
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', marginTop: 6 }}>
                      {overview[subKey].toLocaleString()} {subLabel}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {/* ── Chart ───────────────────────────────────────────── */}
        <div style={{ ...GLASS, padding: '24px', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TrendingUp size={16} color="#A855F7" />
              <span style={{ fontSize: 14, fontWeight: 700 }}>Email Activity</span>
              {tsLoading && <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>Updating…</span>}
            </div>
            <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: 3 }}>
              {DAYS_OPTIONS.map(d => (
                <button key={d} onClick={() => loadTimeSeries(d)} style={{
                  padding: '4px 10px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer', border: 'none', transition: 'all 0.15s', fontFamily: 'inherit',
                  background: d === days ? 'rgba(168,85,247,0.2)' : 'transparent',
                  color: d === days ? '#A855F7' : 'rgba(255,255,255,0.35)',
                  boxShadow: d === days ? '0 0 0 1px rgba(168,85,247,0.3)' : 'none',
                }}>
                  {d}d
                </button>
              ))}
            </div>
          </div>

          {!hasActivity && !loading ? (
            <div style={{ height: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.15)', gap: 8 }}>
              <Mail size={32} />
              <p style={{ margin: 0, fontSize: 13 }}>No activity yet — send your first campaign</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={timeSeries} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={v => v.slice(5)} interval="preserveStartEnd" />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }} formatter={val => <span style={{ color: 'rgba(255,255,255,0.5)' }}>{val}</span>} />
                <Line type="monotone" dataKey="sent"   name="Sent"    stroke="#00D9FF" strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
                <Line type="monotone" dataKey="opened" name="Opened"  stroke="#39FF14" strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
                <Line type="monotone" dataKey="clicked" name="Clicked" stroke="#FF8C42" strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* ── Campaigns table ─────────────────────────────────── */}
        <div style={{ ...GLASS, padding: '24px', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <Send size={15} color="#EC4899" />
            <span style={{ fontSize: 14, fontWeight: 700 }}>Campaigns</span>
            {!loadingCampaigns && campaigns.length > 0 && (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.05)', padding: '2px 8px', borderRadius: 20 }}>
                {campaigns.length}
              </span>
            )}
          </div>

          {loadingCampaigns ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[1,2,3].map(i => <Skeleton key={i} h={44} r={8} />)}
            </div>
          ) : campaigns.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px 0', color: 'rgba(255,255,255,0.2)', fontSize: 13 }}>
              No campaigns yet. Click "Send" on any template to get started.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Campaign', 'Recipients', 'Sent', 'Opens', 'Clicks', 'Bounces', 'Date', 'Status'].map(h => (
                      <th key={h} style={{ textAlign: 'left', color: 'rgba(255,255,255,0.3)', fontWeight: 500, padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(c => {
                    const openRate  = c.stats.sent > 0 ? ((c.stats.opened  / c.stats.sent) * 100).toFixed(1) : null;
                    const clickRate = c.stats.sent > 0 ? ((c.stats.clicked / c.stats.sent) * 100).toFixed(1) : null;
                    const sc = STATUS_STYLE[c.status] || STATUS_STYLE.draft;
                    return (
                      <tr key={c._id} onClick={() => setSelectedCampaign(c)}
                        style={{ cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.12s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.025)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px', color: '#fff', fontWeight: 600, maxWidth: 200 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                        </td>
                        <td style={{ padding: '12px', color: 'rgba(255,255,255,0.5)' }}>{(c.stats.total || 0).toLocaleString()}</td>
                        <td style={{ padding: '12px', color: 'rgba(255,255,255,0.5)' }}>{(c.stats.sent  || 0).toLocaleString()}</td>
                        <td style={{ padding: '12px', color: openRate  ? '#39FF14' : 'rgba(255,255,255,0.25)', fontWeight: openRate  ? 600 : 400 }}>{openRate  ? `${c.stats.opened} (${openRate}%)`  : '—'}</td>
                        <td style={{ padding: '12px', color: clickRate ? '#FF8C42' : 'rgba(255,255,255,0.25)', fontWeight: clickRate ? 600 : 400 }}>{clickRate ? `${c.stats.clicked} (${clickRate}%)` : '—'}</td>
                        <td style={{ padding: '12px', color: 'rgba(255,255,255,0.35)' }}>{c.stats.bounced || '—'}</td>
                        <td style={{ padding: '12px', color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>
                          {c.sentAt ? new Date(c.sentAt).toLocaleDateString() : new Date(c.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', background: sc.bg, color: sc.color }}>
                            {sc.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Template performance ─────────────────────────────── */}
        <div style={{ ...GLASS, padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <BarChart2 size={15} color="#EC4899" />
            <span style={{ fontSize: 14, fontWeight: 700 }}>Template Performance</span>
            {templates.length > 0 && (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.05)', padding: '2px 8px', borderRadius: 20 }}>
                {templates.length}
              </span>
            )}
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[1,2,3].map(i => <Skeleton key={i} h={44} r={8} />)}
            </div>
          ) : templates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px 0', color: 'rgba(255,255,255,0.2)', fontSize: 13 }}>No templates yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Template', 'Category', 'Status', 'Times Used', 'Avg Open', 'Avg Click'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: 'rgba(255,255,255,0.3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid rgba(255,255,255,0.06)', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {templates.map((tmpl, i) => {
                    const cat = tmpl.category || 'general';
                    const color = CAT_COLORS[cat] || '#00D9FF';
                    return (
                      <tr key={tmpl.id || i}
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.12s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.025)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px', color: '#fff', fontWeight: 600 }}>{tmpl.name}</td>
                        <td style={{ padding: '12px' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: `${color}14`, border: `1px solid ${color}33`, color, textTransform: 'capitalize', letterSpacing: '0.03em' }}>{cat}</span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {tmpl.isPublished
                            ? <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(57,255,20,0.08)', border: '1px solid rgba(57,255,20,0.3)', color: '#39FF14' }}>LIVE</span>
                            : <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.35)' }}>DRAFT</span>
                          }
                        </td>
                        <td style={{ padding: '12px', color: tmpl.timesUsed > 0 ? '#00D9FF' : 'rgba(255,255,255,0.25)', fontWeight: tmpl.timesUsed > 0 ? 700 : 400 }}>{tmpl.timesUsed}</td>
                        <td style={{ padding: '12px', color: tmpl.avgOpenRate > 0 ? '#39FF14' : 'rgba(255,255,255,0.25)', fontWeight: tmpl.avgOpenRate > 0 ? 700 : 400 }}>{tmpl.avgOpenRate > 0 ? `${tmpl.avgOpenRate}%` : '—'}</td>
                        <td style={{ padding: '12px', color: tmpl.avgClickRate > 0 ? '#FF8C42' : 'rgba(255,255,255,0.25)', fontWeight: tmpl.avgClickRate > 0 ? 700 : 400 }}>{tmpl.avgClickRate > 0 ? `${tmpl.avgClickRate}%` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Campaign drill-down modal ────────────────────────── */}
      {selectedCampaign && (
        <div onClick={e => e.target === e.currentTarget && setSelectedCampaign(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ ...GLASS, width: 520, maxWidth: '100%', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#fff' }}>{selectedCampaign.name}</h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>{selectedCampaign.subject}</p>
              </div>
              <button onClick={() => setSelectedCampaign(null)} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: 'rgba(255,255,255,0.5)', fontSize: 18, cursor: 'pointer', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
                {[
                  { label: 'Sent',      value: selectedCampaign.stats.sent,      color: '#00D9FF' },
                  { label: 'Delivered', value: selectedCampaign.stats.delivered, color: '#A855F7' },
                  { label: 'Opened',    value: selectedCampaign.stats.opened,    color: '#39FF14' },
                  { label: 'Clicked',   value: selectedCampaign.stats.clicked,   color: '#FF8C42' },
                  { label: 'Bounced',   value: selectedCampaign.stats.bounced,   color: '#f87171' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ textAlign: 'center', background: `${color}0a`, border: `1px solid ${color}22`, borderRadius: 10, padding: '12px 8px' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1, textShadow: `0 0 16px ${color}44` }}>{(value || 0).toLocaleString()}</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 16, padding: '12px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, fontSize: 13 }}>
                <div style={{ color: 'rgba(255,255,255,0.4)', marginBottom: 4, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contact Source</div>
                <div style={{ color: '#fff' }}>{selectedCampaign.contactSource?.label || selectedCampaign.contactSource?.type || '—'}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </div>
  );
};

export default AnalyticsPage;
